import { ContentDetails } from "../../../../../../common/types/ContentDetails";
import { ContentReferenceType } from "../../../../../../common/types/ContentReferenceType";
import {
    CloudFileAssociationAncillaryDetails,
    CloudFileAssociationContentDetails,
    CloudFileAssociationMovieDetails,
    CloudFileAssociationTvSeriesDetails
} from "../context/CloudFileAssociationContentDetails";
import { CloudFileAssociationContentFormState } from "../context/CloudFileAssociationContext";

export const inactiveContentMessage = "Archived or deleted content is selected.";

export const checkIsInactiveContentSelected = (
    fetchedContent?: CloudFileAssociationContentDetails,
    selectedContent?: ContentDetails | CloudFileAssociationContentFormState
): boolean => {
    if (!fetchedContent || !selectedContent) return false;

    switch (fetchedContent.type) {
        case ContentReferenceType.Movie:
            return isMovieContentArchivedOrDeleted(fetchedContent, selectedContent);
        case ContentReferenceType.TvSeries:
            return isTvSeriesContentArchivedOrDeleted(fetchedContent, selectedContent);
        case ContentReferenceType.Ancillary:
            return isAncillaryContentArchivedOrDeleted(fetchedContent);
        default:
            return false;
    }
};

const isMovieContentArchivedOrDeleted = (
    fetchedContent: CloudFileAssociationMovieDetails,
    selectedContent: ContentDetails | CloudFileAssociationContentFormState
) => {
    return (
        fetchedContent.buzz?.isArchived ||
        fetchedContent.buzz?.isDeleted ||
        hasArchivedSelectedVersion(fetchedContent.versions, selectedContent)
    );
};

const isAncillaryContentArchivedOrDeleted = (fetchedContent: CloudFileAssociationAncillaryDetails) => {
    return (fetchedContent.buzz?.isArchived || fetchedContent.buzz?.isDeleted) ?? false;
};

const isTvSeriesContentArchivedOrDeleted = (
    fetchedContent: CloudFileAssociationTvSeriesDetails,
    selectedContent: ContentDetails | CloudFileAssociationContentFormState
) => {
    const episode = getEpisode(fetchedContent, selectedContent);

    return (
        episode?.buzz?.isArchived ||
        episode?.buzz?.isDeleted ||
        hasArchivedSelectedVersion(episode?.versions ?? [], selectedContent)
    );
};

const hasArchivedSelectedVersion = (
    versions: { id: string; buzzVersion?: { isArchived?: boolean } }[],
    selectedContent: ContentDetails | CloudFileAssociationContentFormState
) => {
    return versions.some((v) => v.buzzVersion?.isArchived && includesVersion(selectedContent, v.id));
};

const getEpisode = (
    content: CloudFileAssociationTvSeriesDetails,
    selectedContent: ContentDetails | CloudFileAssociationContentFormState
) => {
    const season = content.seasons?.find((s) => s.id === getSeasonId(selectedContent));

    return season?.episodes.find((e) => e.id === getEpisodeId(selectedContent));
};

const includesVersion = (
    selectedContent: ContentDetails | CloudFileAssociationContentFormState,
    versionId: string
): boolean => {
    if ("versionIds" in selectedContent) {
        return selectedContent.versionIds?.includes(versionId) ?? false;
    }

    if ("versions" in selectedContent) {
        return selectedContent.versions?.some((v) => v.id === versionId) ?? false;
    }

    return false;
};

const getSeasonId = (selectedContent: ContentDetails | CloudFileAssociationContentFormState): string | undefined => {
    if ("season" in selectedContent) {
        return selectedContent.season?.id;
    }

    if ("seasonId" in selectedContent) {
        return selectedContent.seasonId;
    }

    return undefined;
};

const getEpisodeId = (selectedContent: ContentDetails | CloudFileAssociationContentFormState): string | undefined => {
    if ("episode" in selectedContent) {
        return selectedContent.episode?.id;
    }

    if ("episodeId" in selectedContent) {
        return selectedContent.episodeId;
    }

    return undefined;
};
